import { type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { STORAGE_KEY } from '@shared/storage-key';
import { Subject, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AuthApi } from './auth-api';
import { SKIP_AUTH } from './skip-auth-token';

let isRefreshing = false;
let refreshTokenSubject: Subject<string> | null = null;

function getOrCreateRefreshSubject(): Subject<string> {
  if (!refreshTokenSubject || refreshTokenSubject.closed) {
    refreshTokenSubject = new Subject<string>();
  }
  return refreshTokenSubject;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_AUTH)) {
    return next(req);
  }

  const authApi = inject(AuthApi);
  const token = localStorage.getItem(STORAGE_KEY.ATO);

  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error) => {
      if (error.status === 401 && token) {
        if (isRefreshing) {
          const subject = getOrCreateRefreshSubject();
          return subject.pipe(
            take(1),
            switchMap((newToken) => {
              const retryReq = req.clone({
                headers: req.headers.set('Authorization', `Bearer ${newToken}`),
              });
              return next(retryReq);
            }),
          );
        }

        isRefreshing = true;
        const subject = getOrCreateRefreshSubject();

        return authApi.refreshToken().pipe(
          switchMap(({ access_token }) => {
            isRefreshing = false;
            subject.next(access_token);
            subject.complete();
            refreshTokenSubject = null;

            const retryReq = req.clone({
              setHeaders: { Authorization: `Bearer ${access_token}` },
            });
            return next(retryReq);
          }),
          catchError((refreshError) => {
            isRefreshing = false;
            subject.error(refreshError);
            refreshTokenSubject = null;

            authApi.clearSession();
            return throwError(() => refreshError);
          }),
        );
      }
      return throwError(() => error);
    }),
  );
};
